/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useEffect, useRef } from 'react'
import { useAppSelector } from '../../../../reducers';
import { ChartProps, CHART_DEFAULTS, GenericChartProps } from '../utils/constants';
import ChartOrNotWrapper from './ChartOrNotWrapper';
import { chartStyleMap } from '../utils/chartStyles';
import { insightChartSelectorsMap, insightChartSelectorsNames, InsightsChartSelector } from '../utils/selectors';
import { GoogleChartWrapper, ReactGoogleChartEvent } from 'react-google-charts/dist/types';

const getOptionsByType = (chartType: GenericChartProps['chartType']) => {
  switch (chartType) {
    case 'PieChart':
      return {
        pieHole: 0.7,
        pieSliceText: 'label',
        legend: 'none',
        chartArea: {
          top: 10,
          bottom: 10,
          left: 10,
          right: 10,
        }
      }
  }
}

export const getChartOptions = ({
  chartType,
  options,
  vAxisTitle,
}: {
  chartType: GenericChartProps['chartType'];
  options?: GenericChartProps['options'];
  vAxisTitle?: string;
}) => {
  const chartOptions = options ? chartStyleMap[options] : undefined;

  // @TODO - Should do a deep merge
  return {
    ...CHART_DEFAULTS.OPTIONS,
    ...getOptionsByType(chartType),
    ...chartOptions,
    vAxis: {
      title: vAxisTitle,
      format: 'short',
    },
  };
};

function getInsightsChartSelector(name: string): InsightsChartSelector {
  if (insightChartSelectorsNames[name]) {
    return insightChartSelectorsMap[name]
  }
  console.error(`Invalid Chart Selector ${name}`);
  return () => undefined;
}

export const GenericChart = React.memo((props: GenericChartProps) => {
  const { chartType, width = 440, height = 300, options, gridSize } = props;

  const selector = getInsightsChartSelector(props.settingsSelector)
  const datasel = useAppSelector(selector) as ChartProps
  const {
    data,
    utrCodes,
    showNoData,
    vAxisTitle
  } = datasel;

  const ref = useRef<GoogleChartWrapper>();

  useEffect(() => {
    if (ref.current && gridSize?.w && gridSize?.h) {
      ref.current.draw();
    }
  }, [gridSize?.h, gridSize?.w]);

  const chartEvents: ReactGoogleChartEvent[] = [
    {
      eventName: 'ready',
      callback: (ChartRef: { chartWrapper: GoogleChartWrapper }) => {
        ref.current = ChartRef.chartWrapper;
      },
    },
  ];

  return <ChartOrNotWrapper
    utrCodes={utrCodes}
    showNoData={showNoData}
    chartType={chartType}
    data={data}
    width={width}
    height={height}
    options={getChartOptions({ chartType, options, vAxisTitle })}
    chartEvents={chartEvents}
  />
});
